import { ReactNode } from 'react';
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/core';
import { Color } from '../../../../styles/Color';
import { Css, Flex, ITheme, Responsive } from '../../../../ui';
import { RouterLink } from '../../link/RouterLink';
import { Shadow } from '../../../../styles/Shadow';
import { useSearchData } from '../../../../common-data/useSearchData';
import { useGetWebsiteDetails } from '../hooks/useSiteSectionDetails';

export type NavBackgroundVariant = 'dark' | 'light';

interface INavigationWrapperProps {
	isSticky: boolean;
	children: ReactNode;
	isDesktop?: boolean;
	isLogoVisible?: boolean;
	secondary?: ReactNode | ReactNode[];
	className?: string;
}

export function NavigationWrapper({
	isLogoVisible = true,
	isSticky,
	isDesktop = false,
	className,
	secondary,
	children,
}: INavigationWrapperProps) {
	const { homeUrl, logo } = useGetWebsiteDetails();
	const { isSearchOpen } = useSearchData();

	return (
		<StyledMainNavigation
			isDesktop={isDesktop}
			isSticky={isSticky}
			isSearchOpen={isSearchOpen}
			className={`main-navigation-column ${className} `}
		>
			<Container justifyContent="flex-start" alignItems="center">
				{isLogoVisible && (
					<StyledLogoLink
						href={homeUrl}
						aria-label="Go to homepage (redirecting to the homepage)"
					>
						{logo}
					</StyledLogoLink>
				)}
				{children}
			</Container>

			{secondary && secondary}
		</StyledMainNavigation>
	);
}

const Container = styled(Flex)`
	@media (${Responsive.getMediaQueryForBreakpoint('desktop')}) {
		width: 100%;
		justify-content: space-between;
	}
`;
const StyledMainNavigation = styled.div<
	{
		isSticky: boolean;
		isDesktop: boolean;
		isSearchOpen: boolean;
	},
	ITheme
>`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;

	border-radius: 8px;
	color: ${Color.PrimaryWhite};

	padding: ${Css.pxToRem(20)} ${Css.pxToRem(60)};

	${({ isSticky }) =>
		isSticky &&
		css`
			box-shadow: ${Shadow.Small};
			padding: ${Css.pxToRem(20)} ${Css.pxToRem(60)};
		`}

	${({ isSearchOpen }) => isSearchOpen && 'top: 0;'}
		
	@media (${Responsive.getMediaQueryForBreakpoint('desktop')}) {
		display: ${({ isDesktop }) => (isDesktop ? 'none' : 'flex')};
		height: ${Css.pxToRem(113)};
		gap: unset;
	}
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		height: unset;

		padding: 0;
		gap: 0;
	}
`;

const commonLogoStyles = () => css`
	text-decoration: none;
	display: flex;

	color: ${Color.PrimaryWhite};
	svg {
		height: ${Css.pxToRem(36)};
	}
	&:hover {
		color: ${Color.PrimaryWhite};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin: ${Css.pxToRem(13)} 0;
		svg {
			width: ${Css.pxToRem(86)};
			height: ${Css.pxToRem(28)};
		}
	}
`;

export const StyledLogoLink = styled(RouterLink, {
	shouldForwardProp: (props) => isPropValid(props),
})<{}, ITheme>`
	pointer-events: all;
	margin-right: ${Css.pxToRem(34)};
	${commonLogoStyles}

	@media (${Responsive.getMediaQueryForBreakpoint('13inch-navigation')}) {
		margin-right: ${Css.pxToRem(12)};
	}
`;
